import React, { useState, useEffect, useContext } from "react";
import { Icon, Button, Form, Input, Dropdown, Modal } from "semantic-ui-react";
import SemanticDatepicker from "react-semantic-ui-datepickers";
import { useToasts } from "react-toast-notifications";
import { AppContext } from "../../../../Context/AppContext";
import moment from "moment";
import MDL_PROGRESS from "../../../../components/cmp-progress/cmp-progress";
import {
  getCriticalUnitServiceDetails,
  getBuildingLookup,
  createCriticalUnit,
  getPestLookup,
  checkUnitEligibility,
  getIssueTagsLookup,
} from "../../../../constants";
import "./mdl-add-critical-unit.css";

const MDL_ADD_CRITICAL_UNIT = ({ set_mdl_open, setPage, onListRefresh }) => {
  /* VARIABLE DECLARATIONS
	---------------------------------------------------------------------------------*/
  const { userInfo, appVersion, isAppVersionOutdated } = useContext(AppContext);
  const [user] = userInfo;
  const [var_app_version] = appVersion;
  const [, set_is_app_version_outdated] = isAppVersionOutdated;
  const [building_units, setBuildingUnits] = useState([]);
  const [buildingInfo, setBuildingInfo] = useState([]);
  const [selected_building, setSelectedBuilding] = useState([]);
  const [currentDate, setNewDate] = useState(moment().toDate());
  const [ServiceDate, setNewServiceDate] = useState(moment().toDate());
  const [errors, set_errors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [pestData, setPestData] = useState([]);
  const [SelectedPestData, setSelectedPestData] = useState([]);
  const { addToast } = useToasts();
  const [var_mdl_progress, set_mdl_progress] = useState(false);
  const [var_critical_info, set_critical_info] = useState();
  const [currentStep, setCurrentStep] = useState(1); // 1 for initial selection, 2 for details
  const [duplicateExists, setDuplicateExists] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [buildingSearchQuery, setBuildingSearchQuery] = useState("");
  const buildingSearchTimeout = React.useRef(null);
  const [issueTagsOptions, setIssueTagsOptions] = useState([]);

  /* EVENT LISTENERS
	---------------------------------------------------------------------------------*/
  useEffect(() => {
    fetchBuildingInfo("");
    getIssueTagsOptions();
    pestLookup();

    return () => {
      if (buildingSearchTimeout.current) {
        clearTimeout(buildingSearchTimeout.current);
      }
    };
  }, []);

  // useEffect(() => {
  //   if (currentStep === 2) checkForDuplicate();
  // }, [selected_building?.value, building_units, SelectedPestData]);

  /* ASYNC FUNCTIONS
	---------------------------------------------------------------------------------*/
  async function fetchBuildingInfo(searchQuery = "") {
    const requestHeaders = {
      headers: {
        entityid: user.uuid,
        appversion: var_app_version,
      },
    };
    setIsLoading(true);
    try {
      const queryValue = searchQuery ? encodeURIComponent(searchQuery) : "";
      const response = await fetch(
        getBuildingLookup + `?q=${queryValue}`,
        requestHeaders,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch building info");
      }
      const data = await response.json();
      if (data.code === 200 && Array.isArray(data.data)) {
        const formattedBuilding = data.data.map((building) => ({
          key: building.value,
          value: building.value,
          text: `${building.text} (${building.location_number})`,
          customer_name: building.customer_name || "",
          units: building.units || [],
        }));
        setBuildingInfo(formattedBuilding);
      } else {
        setBuildingInfo([]);
        if (data?.version && data?.version !== var_app_version) {
          set_is_app_version_outdated(true);
        }
      }
    } catch (error) {
      console.error("Error fetching building info:", error);
      setBuildingInfo([]);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchBuildingUnits(buildingId) {
    const requestHeaders = {
      headers: {
        entityid: user.uuid,
        appversion: var_app_version,
      },
    };
    try {
      const response = await fetch(
        getBuildingLookup + `?id=${encodeURIComponent(buildingId)}`,
        requestHeaders,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch building units");
      }
      const data = await response.json();
      if (data.code === 200 && data.data?.[0]) {
        return data.data[0].units || [];
      }
    } catch (error) {
      console.error("Error fetching building units:", error);
    }
    return [];
  }

  async function getUnitdata(unit) {
    set_mdl_progress(true);
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token " + user.token,
          entityid: user.uuid,
          appversion: var_app_version,
        },
      };
      const unitNumber = selected_building?.units.find(
        (item) => item.value === unit.toString(),
      )?.text;
      const pestName = var_critical_info?.pest_name || "";
      const response = await fetch(
        getCriticalUnitServiceDetails +
          `?unitNumber=${unitNumber}&locationID=${selected_building?.value}&pestName=${encodeURIComponent(pestName)}`,
        requestOptions,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch unit info");
      }
      const data = await response.json();
      if (data.code === 200) {
        // Update var_critical_info with the new data, ensuring state merges correctly
        // If the API doesn't return a last scheduled service date, leave the
        // field blank instead of showing a stale/default date.
        setNewServiceDate(
          data.data.last_service_date
            ? moment(data.data.last_service_date).toDate()
            : null,
        );
        set_critical_info((prevState) => ({
          ...prevState,
          // tech_reported: data.data.tech_reported || "",
          last_tech_comment: data.data.last_tech_comment || "",
        }));
      } else {
        if (data?.version && data?.version !== var_app_version) {
          set_is_app_version_outdated(true);
        }
        setNewServiceDate(null);
        set_critical_info((prevState) => ({
          ...prevState,
          // tech_reported: "",
          last_service_date: null,
          last_tech_comment: "",
        }));
        console.log("Error:", data.message);
      }
      set_mdl_progress(false);
    } catch (error) {
      setNewServiceDate(null);
      set_mdl_progress(false);
      console.error("Fetch error:", error);
    }
  }

  async function getIssueTagsOptions() {
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token " + user.token,
          entityid: user.uuid,
          appversion: var_app_version,
        },
      };
      const response = await fetch(getIssueTagsLookup, requestOptions);
      if (!response.ok) {
        throw new Error("Failed to fetch issue tags lookup");
      }
      const data = await response.json();
      if (data.code === 200) {
        setIssueTagsOptions(data.data);
      } else {
        if (data?.version && data?.version !== var_app_version) {
          set_is_app_version_outdated(true);
        }
      }
    } catch (error) {
      console.error("Error fetching issue tags lookup:", error);
    }
  }

  async function pestLookup() {
    set_mdl_progress(true);
    const requestHeaders = {
      headers: {
        entityid: user.uuid,
        appversion: var_app_version,
      },
    };
    await fetch(getPestLookup, requestHeaders)
      .then((response) => response.json())
      .then((res) => {
        if (res.code === 200) {
          setPestData(res?.data);
        } else {
          if (res?.version && res?.version !== var_app_version) {
            set_is_app_version_outdated(true);
          }
          setPestData(null);
        }
        set_mdl_progress(false);
      })
      .catch((error) => {
        set_mdl_progress(false);
        setPestData(null);
        console.error("Error:", error);
      });
  }

  async function checkForDuplicate() {
    var temp_error = { ...errors };
    var isValid = true;
    if (selected_building.length === 0) {
      temp_error.selected_building = "Please select building.";
      isValid = false;
    }
    if (building_units.length === 0) {
      temp_error.buildingunits = "Please select units.";
      isValid = false;
    }
    if (!SelectedPestData || SelectedPestData.length === 0) {
      temp_error.pest = "Please select pest.";
      isValid = false;
    }
    if (!isValid) {
      set_errors(temp_error);
      return;
    }
    set_errors({});
    set_mdl_progress(true);
    try {
      const cleanBuildingId = selected_building?.value?.toString().trim() || "";
      const unitNumber =
        selected_building?.units?.find(
          (item) => item.value === building_units.toString(),
        )?.text || building_units.toString();
      const pestName = var_critical_info?.pest_name;
      const url = new URL(checkUnitEligibility);
      url.searchParams.append("buildingID", cleanBuildingId);
      url.searchParams.append("unit_number", unitNumber);
      url.searchParams.append("pest_name", pestName);
      setDuplicateInfo({
        buildingID: selected_building?.text,
        unit_number: unitNumber,
        pest_name: pestName,
      });
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token " + user.token,
          entityid: user.uuid,
          appversion: var_app_version,
        },
      };
      const response = await fetch(url.toString(), requestOptions);
      if (!response.ok) {
        throw new Error(
          `API Error: ${response.status} - ${response.statusText}`,
        );
      }
      const data = await response.json();
      if (data.code === 200) {
        if (data?.duplicate) {
          setDuplicateExists(true);
          addToast(data?.success, {
            appearance: "warning",
            autoDismiss: true,
          });
        } else {
          // Step 2 only renders once this finishes — the Next button already
          // shows a loading state (var_mdl_progress) while we wait.
          await getUnitdata(building_units);
          setDuplicateExists(false);
          setDuplicateInfo(null);
          setCurrentStep(2);
        }
      } else {
        if (data?.version && data?.version !== var_app_version) {
          set_is_app_version_outdated(true);
        }
        addToast(data?.message || data?.failed || "Something went wrong", {
          appearance: "error",
          autoDismiss: true,
        });
        set_mdl_open(false);
      }
    } catch (error) {
      console.error("Error checking for duplicate:", error);
      addToast("Error checking for duplicate unit. Please try again.", {
        appearance: "error",
        autoDismiss: true,
      });
    } finally {
      set_mdl_progress(false);
    }
  }
  /* EVENT FUNCTIONS
	---------------------------------------------------------------------------------*/

  function isValidated() {
    var temp_error = {};
    var isValid = true;
    if (!currentDate) {
      temp_error.dateReportedDate = "Please select date.";
      isValid = false;
    }
    if (!selected_building?.value) {
      temp_error.selected_building = "Please select building.";
      isValid = false;
    }
    if (
      !building_units ||
      (Array.isArray(building_units) && building_units.length === 0)
    ) {
      temp_error.buildingunits = "Please select units.";
      isValid = false;
    }

    if (!SelectedPestData || SelectedPestData.length === 0) {
      temp_error.pest = "Please select pest.";
      isValid = false;
    }

    // const summaryValue = (var_critical_info?.summary || "").trim();
    // if (!summaryValue) {
    //   temp_error.summary = "Please add summary.";
    //   isValid = false;
    // } else if (summaryValue.length > 2000) {
    //   temp_error.summary = "Summary Character limit exceeded.";
    //   isValid = false;
    // }

    if (!ServiceDate) {
      temp_error.lastScheduledDate = "Please select service date.";
      isValid = false;
    }

    // Note is optional — only validate character limit when note has content
    const noteValue = var_critical_info?.note || "";
    if (noteValue.length > 2000) {
      temp_error.note = "Note Character limit exceeded.";
      isValid = false;
    }

    const lastTechValue = (var_critical_info?.last_tech_comment || "").trim();
    if (!lastTechValue) {
      temp_error.last_tech_comment = "Please add last tech comment.";
      isValid = false;
    } else if (lastTechValue.length > 1000) {
      temp_error.last_tech_comment =
        "Last Tech Comment Character limit exceeded.";
      isValid = false;
    }

    if (isValid) {
      set_errors({});
    } else {
      set_errors(temp_error);
    }
    return isValid;
  }

  function onChangeUnits(event, { value }) {
    // var temp_error = { ...errors };
    // temp_error.buildingunits = null;
    // set_errors(temp_error);
    // const filteredArray = selected_building?.units?.filter(unit => value.includes(unit.value));
    // getUnitdata is called on "Next" (checkForDuplicate) instead — by then
    // building/unit/pest are all selected, so the API gets the real pest name.
    setBuildingUnits(value);
  }

  const convertDateFormat = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString();
  };

  async function handleCriticalUnitDetails() {
    if (isValidated()) {
      set_mdl_progress(true);
      var_critical_info.buildingID = selected_building?.value;
      var_critical_info.unit_number = selected_building?.units.find(
        (item) => item.value === building_units.toString(),
      )?.text;
      var_critical_info.date_reported = convertDateFormat(currentDate).replace(
        "Z",
        "",
      );
      var_critical_info.last_service_date = convertDateFormat(
        ServiceDate,
      ).replace("Z", "");
      var_critical_info.status = "ongoing";
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token " + user.token,
          entityid: user.uuid,
          appversion: var_app_version,
        },
        body: JSON.stringify(var_critical_info),
      };
      await fetch(createCriticalUnit, requestOptions)
        .then((response) => response.json())
        .then((res) => {
          set_mdl_open(false);
          if (res.code && res.code === 401) {
            addToast("Session expired. Please Login again.", {
              appearance: "error",
              autoDismiss: true,
            });
          } else {
            if (res.code === 200) {
              addToast(res.success, {
                appearance: "success",
                autoDismiss: true,
              });
              if (typeof onListRefresh === "function") {
                onListRefresh();
              } else {
                setPage(1);
              }
            } else {
              if (res?.version && res?.version !== var_app_version) {
                set_is_app_version_outdated(true);
              }
              addToast(res.failed, {
                appearance: "error",
                autoDismiss: true,
              });
            }
          }
        })
        .catch((err) => {
          console.error("Error:", err);
        });
    }
    set_mdl_progress(false);
  }

  const onChange = (event, data) => {
    if (data?.value) {
      try {
        // Parse the date using moment and convert it to a Date object
        const parsedDate = moment(data.value).toDate();

        // If the date is valid, update the state and reset the error
        if (!isNaN(parsedDate.getTime())) {
          var temp_error = { ...errors };
          temp_error.currentDate = null; // Reset the specific error
          set_errors(temp_error);
          setNewDate(parsedDate); // Set the date directly as a Date object
        } else {
          throw new Error("Invalid date");
        }
      } catch (error) {
        console.error("Date parsing error:", error.message);
      }
    }
  };

  const onChangeLastServiceDate = (event, data) => {
    if (data?.value) {
      try {
        // Parse the date using moment and convert it to a Date object
        const parsedDate = moment(data.value).toDate();

        // If the date is valid, update the state and reset the error
        if (!isNaN(parsedDate.getTime())) {
          var temp_error = { ...errors };
          temp_error.lastScheduledDate = null; // Reset the specific error
          set_errors(temp_error);
          setNewServiceDate(parsedDate); // Set the date directly as a Date object
        } else {
          throw new Error("Invalid date");
        }
      } catch (error) {
        console.error("Date parsing error:", error.message);
      }
    }
  };

  // const onChangeInput = (event) => {
  //   let name = event.target.name;
  //   let value = event.target.value;
  // }

  const handleSearchChange = (event, data) => {
    const searchQuery = data?.searchQuery ?? "";
    setBuildingSearchQuery(searchQuery);
    if (buildingSearchTimeout.current) {
      clearTimeout(buildingSearchTimeout.current);
    }
    buildingSearchTimeout.current = setTimeout(() => {
      fetchBuildingInfo(searchQuery);
    }, 300);
  };

  const handleBuildingDropdownOpen = () => {
    // Always show default building list when opening with no search text
    if (!buildingSearchQuery.trim()) {
      fetchBuildingInfo("");
    }
  };

  const onChangeBuilding = async (event, { value }) => {
    var tempError = { ...errors };
    tempError.selected_building = null;
    set_errors(tempError);
    setBuildingUnits([]);

    const building = buildingInfo.find((item) => item.value === value);
    if (!building) {
      setSelectedBuilding(null);
      return;
    }

    setSelectedBuilding(building);
    setBuildingSearchQuery("");

    // Units are loaded on select so the building list can open quickly
    if (!building.units?.length) {
      const units = await fetchBuildingUnits(value);
      const formattedUnits = (units || []).map((unit) => ({
        key: unit.value,
        value: unit.value,
        text: unit.text,
      }));
      setSelectedBuilding((prev) =>
        prev?.value === value ? { ...prev, units: formattedUnits } : prev,
      );
      setBuildingInfo((prev) =>
        prev.map((item) =>
          item.value === value ? { ...item, units: formattedUnits } : item,
        ),
      );
    }
  };

  const FIELD_CHAR_LIMITS = {
    summary: 2000,
    note: 2000,
    last_tech_comment: 1000,
    csr_actions: 500,
    ss_rm_steps: 500,
  };

  const onChangeInput = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    const limit = FIELD_CHAR_LIMITS[name] ?? 2000;

    // Only show character-limit error when text actually reaches/exceeds the limit
    if (value.length > 0 && value.length >= limit) {
      set_errors((prevErrors) => ({
        ...prevErrors,
        [name]: "Character limit exceeded",
      }));
    } else {
      set_errors((prevErrors) => {
        const nextErrors = { ...prevErrors };
        if (
          nextErrors[name] === "Character limit exceeded" ||
          nextErrors[name]?.includes("Character limit exceeded")
        ) {
          delete nextErrors[name];
        }
        return nextErrors;
      });
    }

    set_critical_info({ ...var_critical_info, [name]: value });
  };

  const renderCharLimitHint = (fieldName) => {
    const limit = FIELD_CHAR_LIMITS[fieldName] ?? 2000;
    const length = var_critical_info?.[fieldName]?.length || 0;
    const remaining = Math.max(0, limit - length);
    const isLimitExceeded = length >= limit;

    return (
      <div
        className={fieldName === "last_tech_comment" ? "char-limit-hint" : undefined}
        style={{
          textAlign: "right",
          color: isLimitExceeded ? "red" : "gray",
        }}
      >
        {isLimitExceeded
          ? "Character limit exceeded"
          : `${remaining} characters left`}
      </div>
    );
  };

  function onChangePest(event, data) {
    let id = data.value;
    let name = data.name;
    let value = data.options?.find((opt) => opt.value === id)?.text;
    var temp_error = { ...errors };
    temp_error.pest = null;
    set_errors(temp_error);
    setSelectedPestData(id);
    set_critical_info({ ...var_critical_info, [name]: value });
  }

  function onChangeIssueTags(event, { value }) {
    set_critical_info({ ...var_critical_info, issue_tags: value });
  }

  /* RENDER APP
	---------------------------------------------------------------------------------*/
  return (
    <>
      <div className="modal-header">
        <div className="modal-header-title">Add Critical Unit Details</div>
        <div
          className="modal-header-close"
          onClick={(e) => {
            e.stopPropagation();
            set_mdl_open(false);
          }}
        >
          <Icon name="times circle outline" />
        </div>
      </div>
      <div className="modal-content  fields">
        <div className="critical-add-step1-grid">
          <div className="modal-field">
            <label htmlFor="building_dropdown">
              Location ID or Building Address:{" "}
            </label>
            <Dropdown
              search={(options) => options}
              selection
              selectOnBlur={false}
              className="building_dropdown"
              id="building_dropdown"
              name="sortby_options"
              placeholder="Select Building"
              label="Building Address: "
              searchQuery={buildingSearchQuery}
              onSearchChange={handleSearchChange}
              onOpen={handleBuildingDropdownOpen}
              onChange={onChangeBuilding}
              value={selected_building?.value || null}
              options={buildingInfo}
              error={errors.selected_building}
              disabled={currentStep === 2}
              loading={isLoading}
              noResultsMessage={
                isLoading ? "Loading buildings..." : "No buildings found"
              }
            />
          </div>

          <div className="modal-field">
            <label htmlFor="client">Client: </label>
            <Form.Field
              id="client"
              required={true}
              name="client"
              placeholder="Client"
              style={{ width: "100%" }}
              disabled={true}
              control={Input}
              value={selected_building?.customer_name}
            />
          </div>

          <div className="modal-field">
            <label htmlFor="sortby_units">Unit: </label>
            <Dropdown
              search
              style={{ width: "100%" }}
              selection
              upward
              className="sortby_units"
              id="sortby_units"
              name="sortby_units"
              placeholder="Select Units"
              label="Units: "
              onChange={onChangeUnits}
              value={building_units}
              error={errors.buildingunits}
              options={selected_building?.units}
              disabled={
                selected_building?.value
                  ? currentStep === 2
                    ? true
                    : false
                  : true
              }
              selectOnBlur={false}
            />
          </div>

          <div className="modal-field">
            <label htmlFor="pest_name">Pest: </label>
            <Dropdown
              search
              style={{ width: "100%" }}
              selection
              upward
              className="pest_name"
              id="pest_name"
              name="pest_name"
              placeholder="Select Pest"
              label="Pests: "
              onChange={onChangePest}
              error={errors.pest}
              value={SelectedPestData}
              options={pestData}
              disabled={currentStep === 2}
              selectOnBlur={false}
            />
          </div>
        </div>

        {/* Error message for duplicate critical unit */}
        {duplicateExists && (
          <div
            style={{
              marginTop: "15px",
              marginBottom: "15px",
              padding: "16px 20px",
              backgroundColor: "#fff6f6",
              borderLeft: "4px solid #be3836",
              borderRight: "1px solid #e0b0b0",
              borderTop: "1px solid #e0b0b0",
              borderBottom: "1px solid #e0b0b0",
              borderRadius: "4px",
              color: "#be3836",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "6px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontWeight: "600",
                  fontSize: "13px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    gap: "5px",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "#be3836",
                      color: "white",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "14px",
                      fontWeight: "700",
                    }}
                  >
                    !
                  </div>
                  <strong>Duplicate Record</strong>
                </span>
              </div>
              <span
                style={{
                  backgroundColor: "#be3836",
                  color: "white",
                  padding: "2px 10px",
                  borderRadius: "12px",
                  fontSize: "11px",
                  fontWeight: "600",
                }}
              >
                Conflict
              </span>
            </div>
            <div style={{ fontWeight: "500", fontSize: "14px" }}>
              <strong>{duplicateInfo?.buildingID}</strong> already has a
              critical Unit # <strong>{duplicateInfo?.unit_number}</strong> with
              Pest <strong>{duplicateInfo?.pest_name}</strong>.
            </div>
          </div>
        )}

        {/* Remaining form fields - only show on step 2 */}
        {currentStep === 2 && (
          <div className="critical-add-step2-grid">
            <div className="modal-field">
              <label htmlFor="dateReportedDate">Date Reported: </label>
              <SemanticDatepicker
                style={{ width: "100%" }}
                id="dateReportedDate"
                name="dateReportedDate"
                placeholder={"Date Reported"}
                fluid
                value={currentDate}
                error={errors?.dateReportedDate}
                onChange={onChange}
              />
            </div>

            <div className="modal-field">
              <label htmlFor="lastScheduledDate">
                Last Scheduled Service Date:{" "}
              </label>
              <SemanticDatepicker
                style={{ width: "100%" }}
                id="lastScheduledDate"
                name="lastScheduledDate"
                placeholder={"Date"}
                required={true}
                fluid
                value={ServiceDate}
                error={errors?.lastScheduledDate}
                onChange={onChangeLastServiceDate}
              />
            </div>

            {/* <div className="modal-field">
              <label htmlFor="tech_reported">Tech Reported: </label>
              <Form.Field
                id="tech_reported"
                name="tech_reported"
                placeholder="Tech Reported"
                required={true}
                style={{
                  width: "100%",
                  borderColor: errors["tech_reported"] ? "red" : "black",
                  backgroundColor: errors["tech_reported"] ? "#fff6f6" : "white",
                }}
                control={Input}
                error={errors["tech_reported"]}
                value={var_critical_info?.tech_reported}
                onChange={onChangeInput}
              />
            </div> */}

            <div className="modal-field modal-field-full">
              <label htmlFor="issue_tags">Issue Tags: </label>
              <Dropdown
                multiple
                selection
                search
                style={{ width: "100%" }}
                className="issue_tags"
                id="issue_tags"
                name="issue_tags"
                placeholder="Select Issue Tags"
                noResultsMessage="All tags selected"
                onChange={onChangeIssueTags}
                value={var_critical_info?.issue_tags || []}
                options={issueTagsOptions.map((opt) => ({
                  ...opt,
                  content: (
                    <span style={{ display: "flex", alignItems: "center" }}>
                      <span
                        style={{
                          display: "inline-block",
                          width: "12px",
                          height: "12px",
                          borderRadius: "2px",
                          backgroundColor: opt.color || "#e0e0e0",
                          border: "1px solid rgba(0,0,0,0.15)",
                          marginRight: "8px",
                        }}
                      />
                      {opt.text}
                    </span>
                  ),
                }))}
                renderLabel={(item) => ({
                  content: item.text,
                  style: {
                    backgroundColor: item.color || "#e0e0e0",
                    color: "#333",
                  },
                })}
                selectOnBlur={false}
              />
            </div>

            {/* <div className="modal-field modal-field-full">
              <label htmlFor="summary">Summary of Issue: </label>
              <Form.Field>
                <Form.TextArea
                  id="summary"
                  style={{
                    width: "100%",
                    // height: "40px",
                    borderColor: errors["summary"] ? "red" : "black",
                    backgroundColor: errors["summary"] ? "#fff6f6" : "white",
                  }}
                  name="summary"
                  required={true}
                  value={var_critical_info?.summary}
                  error={errors["summary"]}
                  onChange={onChangeInput}
                />
                {renderCharLimitHint("summary")}
              </Form.Field>
            </div> */}

            {/* <div className="modal-field modal-field-full">
              <label htmlFor="csr_actions">CSR's Action: </label>
              <Form.Field>
                <Form.TextArea
                  id="csr_actions"
                  style={{
                    width: "100%",
                    height: "40px",
                    borderColor: errors["csr_actions"] ? "red" : "black",
                    backgroundColor: errors["csr_actions"] ? "#fff6f6" : "white",
                  }}
                  name="csr_actions"
                  required={true}
                  value={var_critical_info?.csr_actions}
                  error={errors["csr_actions"]}
                  onChange={onChangeInput}
                />
                <div
                  style={{
                    textAlign: "right",
                    color:
                      var_critical_info?.csr_actions?.length > 500
                        ? "red"
                        : "gray",
                  }}
                >
                  {errors["csr_actions"]
                    ? 0
                    : 500 - (var_critical_info?.csr_actions?.length || 0)}{" "}
                  characters left
                </div>
              </Form.Field>
            </div>
            <div className="modal-field modal-field-full">
              <label htmlFor="ss_rm_steps">SS/RM Next Step: </label>
              <Form.Field>
                <Form.TextArea
                  id="ss_rm_steps"
                  style={{ width: "100%", height: "40px" }}
                  name="ss_rm_steps"
                  required={true}
                  value={var_critical_info?.ss_rm_steps}
                  error={errors["ss_rm_steps"]}
                  onChange={onChangeInput}
                />
                <div
                  style={{
                    textAlign: "right",
                    color:
                      var_critical_info?.ss_rm_steps?.length > 500
                        ? "red"
                        : "gray",
                  }}
                >
                  {errors["ss_rm_steps"]
                    ? 0
                    : 500 - (var_critical_info?.ss_rm_steps?.length || 0)}{" "}
                  characters left
                </div>
              </Form.Field>
            </div> */}

            <div className="modal-field modal-field-full">
              <label htmlFor="note">Note: </label>
              <Form.Field>
                <Form.TextArea
                  id="note"
                  style={{
                    width: "100%",
                    // height: "40px",
                    borderColor: errors["note"] ? "red" : "black",
                    backgroundColor: errors["note"] ? "#fff6f6" : "white",
                  }}
                  name="note"
                  value={var_critical_info?.note}
                  onChange={onChangeInput}
                  error={errors["note"]}
                />
                {renderCharLimitHint("note")}
              </Form.Field>
            </div>

            <div className="modal-field modal-field-full">
              <label htmlFor="last_tech_comment">Last Tech Comment: </label>
              <Form.Field>
                <Form.TextArea
                  id="last_tech_comment"
                  width={20}
                  style={{
                    width: "100%",
                    height: "110px",
                    borderColor: errors["last_tech_comment"] ? "red" : "black",
                    backgroundColor: errors["last_tech_comment"]
                      ? "#fff6f6"
                      : "white",
                  }}
                  name="last_tech_comment"
                  value={var_critical_info?.last_tech_comment}
                  error={errors["last_tech_comment"]}
                  onChange={onChangeInput}
                />
                {renderCharLimitHint("last_tech_comment")}
              </Form.Field>
            </div>
          </div>
        )}
      </div>
      <div className="modal-footer">
        {currentStep === 2 && (
          <Button
            type="button"
            onClick={() => {
              setCurrentStep(1);
              setDuplicateExists(false);
              setDuplicateInfo(null);
              set_errors({});
              set_critical_info({
                ...var_critical_info,
                // tech_reported: "",
                summary: "",
                note: "",
              });
            }}
            disabled={var_mdl_progress}
          >
            Back
          </Button>
        )}
        <Button
          type="submit"
          color="red"
          onClick={() => {
            if (currentStep === 1) {
              checkForDuplicate();
            } else if (currentStep === 2) {
              handleCriticalUnitDetails();
            }
          }}
          disabled={var_mdl_progress}
          loading={var_mdl_progress}
        >
          {currentStep === 1 ? "Next" : "Add"}
        </Button>
      </div>
      {/***** MODAL: PROGRESS **************************************************************************/}
      <Modal
        id="mdl-progress"
        dimmer={"inverted"}
        open={var_mdl_progress}
        closeOnDimmerClick={false}
        closeOnEscape={false}
        onClose={() => {}}
      >
        <MDL_PROGRESS
          set_mdl_open={set_mdl_progress}
          var_modaltitle="Please wait"
          var_message={"Stay on this page until this process finish."}
        ></MDL_PROGRESS>
      </Modal>

      {/***** END MODAL: PROGRESS **************************************************************************/}
    </>
  );
};
export default MDL_ADD_CRITICAL_UNIT;
